import { createFileRoute, Outlet, useNavigate, Link, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { getPaymentEnvironment } from "@/lib/paddle";
import { Button } from "@/components/ui/button";

import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { SupportBanner } from "@/components/SupportBanner";
import { SupportPresenceBanner } from "@/components/SupportPresenceBanner";
import { SidebarAdsCarousel } from "@/components/layout/SidebarAdsCarousel";
import { SubscriptionStatusPanel } from "@/components/SubscriptionStatusPanel";
import { OwnerCompanyProvider } from "@/owner/OwnerCompanyContext";
import { OwnerCompanyBar } from "@/owner/OwnerCompanyBar";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  FileText,
  FileSignature,
  HardHat,
  MapPin,
  Clock,
  Wallet,
  Receipt,
  Package,
  TrendingUp,
  Sparkles,
  UserCog,
  Settings,
  Activity,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ShoppingCart,
  Hammer,
  UsersRound,
  DollarSign,
  ShieldCheck,
  HelpCircle,
  FileBarChart,
  LifeBuoy,
  Crown,
  Mail,
} from "lucide-react";
import logo from "@/assets/logo.webp";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Private workspace for Business Dite Ads customers." },
    ],
  }),
  component: AppLayout,
});

type NavTo =
  | "/dashboard"
  | "/clients"
  | "/estimates"
  | "/change-orders"
  | "/contracts"
  | "/invoices"
  | "/projects"
  | "/job-sites"
  | "/time-tracking"
  | "/payroll"
  | "/payroll/my-paystubs"
  | "/expenses"
  | "/materials"
  | "/cash-flow"
  | "/team"
  | "/gps-analytics"
  | "/reports"
  | "/settings"
  | "/help"
  | "/support"
  | "/plans"
  | "/admin/support"
  | "/admin/accounts"
  | "/admin/ads"
  | "/admin/marketing";

/**
 * Nível de acesso de cada tela. Hierarquia: owner ⊇ admin ⊇ supervisor ⊇ (funcionário).
 * - "all"        → qualquer usuário autenticado da equipe (inclui funcionário)
 * - "supervisor" → supervisor + admin + owner
 * - "admin"      → admin + owner
 * - "owner"      → só owner
 * Funcionário fica só com as telas "all" (Ponto + Ajuda).
 */
type Access = "all" | "supervisor" | "admin" | "owner";

type NavItem = {
  labelKey: string;
  icon: typeof LayoutDashboard;
  to?: NavTo;
  access?: Access; // default "all"
};

type NavSection = {
  labelKey: string;
  icon: typeof LayoutDashboard;
  to?: NavTo;
  access?: Access; // default "all"
  items?: NavItem[];
};

const navSections: NavSection[] = [
  {
    labelKey: "nav.dashboard",
    icon: LayoutDashboard,
    to: "/dashboard",
    access: "admin", // supervisor NÃO vê dashboard (só Ponto/Obras/Materiais/Despesas/GPS/Ajuda)
  },
  {
    labelKey: "nav.help",
    icon: HelpCircle,
    items: [
      { labelKey: "nav.helpCenter", icon: HelpCircle, to: "/help" },
      { labelKey: "nav.support", icon: LifeBuoy, to: "/support", access: "admin" },
      { labelKey: "nav.adminSupport", icon: LifeBuoy, to: "/admin/support", access: "owner" },
    ],
  },
  {
    labelKey: "nav.sales",
    icon: ShoppingCart,
    access: "admin",
    items: [
      { labelKey: "nav.clients", icon: Users, to: "/clients", access: "admin" },
      { labelKey: "nav.estimates", icon: Sparkles, to: "/estimates", access: "admin" },
      { labelKey: "nav.contracts", icon: FileSignature, to: "/contracts", access: "admin" },
      { labelKey: "nav.changeOrders", icon: FileSignature, to: "/change-orders", access: "admin" },
      { labelKey: "nav.invoices", icon: Receipt, to: "/invoices", access: "admin" },
    ],
  },
  {
    labelKey: "nav.operations",
    icon: Hammer,
    access: "supervisor", // supervisor vê Obras + Materiais (só leitura); Projetos é admin
    items: [
      { labelKey: "nav.projects", icon: HardHat, to: "/projects", access: "admin" },
      { labelKey: "nav.jobSites", icon: MapPin, to: "/job-sites", access: "supervisor" },
      { labelKey: "nav.materials", icon: Package, to: "/materials", access: "supervisor" },
    ],
  },
  {
    labelKey: "nav.workforce",
    icon: UsersRound,
    items: [
      // Ponto: funcionário bate; supervisor/admin também aprovam as horas aqui.
      { labelKey: "nav.timeTracking", icon: Clock, to: "/time-tracking", access: "all" },
      { labelKey: "nav.payroll", icon: Wallet, to: "/payroll", access: "admin" },
      { labelKey: "nav.myPaystubs", icon: Wallet, to: "/payroll/my-paystubs", access: "admin" },
    ],
  },
  {
    labelKey: "nav.finance",
    icon: DollarSign,
    access: "supervisor", // supervisor aprova despesas
    items: [
      { labelKey: "nav.expenses", icon: FileText, to: "/expenses", access: "supervisor" },
      { labelKey: "nav.cashFlow", icon: TrendingUp, to: "/cash-flow", access: "admin" },
    ],
  },
  {
    labelKey: "nav.admin",
    icon: ShieldCheck,
    items: [
      { labelKey: "nav.team", icon: UserCog, to: "/team", access: "admin" },
      { labelKey: "nav.gpsAnalytics", icon: Activity, to: "/gps-analytics", access: "supervisor" },
      { labelKey: "nav.reports", icon: FileBarChart, to: "/reports", access: "admin" },
      { labelKey: "nav.plans", icon: Sparkles, to: "/plans", access: "admin" },
      { labelKey: "nav.adminAccounts", icon: Crown, to: "/admin/accounts", access: "owner" },
      { labelKey: "nav.adminAds", icon: Sparkles, to: "/admin/ads", access: "owner" },
      { labelKey: "nav.adminMarketing", icon: Mail, to: "/admin/marketing", access: "owner" },
      { labelKey: "nav.settings", icon: Settings, to: "/settings", access: "admin" },
    ],
  },
];

/**
 * Rotas com o nível de acesso EFETIVO. Usado pelo guard central:
 * esconder do menu não basta, tem que bloquear a URL direta.
 */
const ROUTE_ACCESS: { to: string; access: Access }[] = navSections.flatMap((s) => {
  if (s.to && !s.items) return [{ to: s.to, access: s.access ?? "all" }];
  return (s.items ?? [])
    .filter((i) => !!i.to)
    .map((i) => ({ to: i.to as string, access: i.access ?? "all" }));
});

function AppLayout() {
  const { session, loading, company, profile, roles, signOut, hasRole, user } = useAuth();
  const { hasAccess, tier, loading: subLoading } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const { t } = useTranslation();
  const isAdmin = hasRole("admin");
  const isOwner = hasRole("owner");
  const isSupervisor = hasRole("supervisor");
  // Resolve se o usuário atual alcança um nível de acesso. owner ⊇ admin ⊇ supervisor ⊇ all.
  const canAccess = (access: Access = "all") => {
    if (isOwner) return true;
    switch (access) {
      case "all":
        return true;
      case "supervisor":
        return isAdmin || isSupervisor;
      case "admin":
        return isAdmin;
      case "owner":
        return false;
    }
  };
  const primaryRole = isOwner ? "owner" : (roles[0] ?? null);
  const roleBadgeClass: Record<string, string> = {
    owner: "bg-gradient-to-r from-amber-500 to-orange-500 text-white",
    admin: "bg-primary text-primary-foreground",
    supervisor: "bg-accent text-accent-foreground",
    estimate: "bg-secondary text-secondary-foreground",
    employee: "bg-muted text-muted-foreground",
    client: "bg-muted text-muted-foreground",
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-open the section that contains the current route
  const activeSectionLabel = useMemo(() => {
    for (const section of navSections) {
      if (section.to && location.pathname.startsWith(section.to)) return section.labelKey;
      if (section.items?.some((i) => i.to && location.pathname.startsWith(i.to))) {
        return section.labelKey;
      }
    }
    return null;
  }, [location.pathname]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (activeSectionLabel) {
      setOpenSections((prev) => ({ ...prev, [activeSectionLabel]: true }));
    }
  }, [activeSectionLabel]);

  useEffect(() => {
    if (!loading && !session) {
      navigate({ to: "/auth" });
    }
  }, [session, loading, navigate]);

  // Gate the entire app behind an active subscription OR active trial (owners bypass)
  useEffect(() => {
    if (!loading && session && !subLoading && !hasAccess && !isOwner) {
      navigate({ to: "/upgrade" });
    }
  }, [loading, session, subLoading, hasAccess, isOwner, navigate]);

  // Home por papel: só quem alcança o Dashboard (access "admin" → admin/owner) cai nele.
  // Supervisor e funcionário não acessam o Dashboard, então o "lar" deles é o Ponto.
  // (Precisa casar com o access do Dashboard, senão o redirect entra em loop.)
  const homeRoute: NavTo = canAccess("admin") ? "/dashboard" : "/time-tracking";

  // Guard de acesso por rota: esconder do menu não basta — bloqueia a URL direta.
  // Pega o match de rota mais específico e redireciona quem não alcança o nível dela.
  useEffect(() => {
    if (loading || !session) return;
    const match = ROUTE_ACCESS.filter(
      (r) => location.pathname === r.to || location.pathname.startsWith(`${r.to}/`),
    ).sort((a, b) => b.to.length - a.to.length)[0];
    if (!match) return;
    if (!canAccess(match.access)) {
      navigate({ to: homeRoute });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, loading, session, isAdmin, isOwner, isSupervisor, navigate, homeRoute]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const openPortal = async () => {
    if (!user) return;
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaymentEnvironment() },
      });
      if (error) throw new Error(error.message);
      // Sem assinatura ativa (ex.: ainda em trial) — manda para a página de upgrade
      if (data?.noSubscription) {
        toast.info(t("subscription.none"));
        navigate({ to: "/upgrade" });
        return;
      }
      if (!data?.url) throw new Error("No URL returned");
      const win = window.open(data.url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup bloqueado — navega na mesma aba como fallback
        window.location.href = data.url;
      }
    } catch (e) {
      console.error("[openPortal]", e);
      toast.error(t("subscription.portalError"));
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading || !session || subLoading || (!hasAccess && !isOwner)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <OwnerCompanyProvider>
      <PaymentTestModeBanner />
      <SupportBanner />
      <SupportPresenceBanner />
      <div className="flex min-h-screen bg-muted/30">
        {/* Mobile top bar */}
        <div className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background px-4 lg:hidden">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src={logo}
              alt=""
              className="h-10 w-10 rounded-md bg-white object-contain p-0.5 shadow-sm ring-1 ring-border"
            />
            <span className="text-sm font-semibold">{company?.name ?? "Business Dite Ads"}</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={t("nav.toggleMenu")}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Sidebar */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-border bg-sidebar transition-transform lg:translate-x-0",
            mobileOpen ? "translate-x-0" : "-translate-x-full",
            "pt-14 lg:pt-0",
          )}
        >
          <div className="hidden h-16 items-center gap-3 border-b border-sidebar-border px-5 lg:flex">
            <img
              src={logo}
              alt=""
              className="h-11 w-11 rounded-lg bg-white object-contain p-1 shadow-sm ring-1 ring-border"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-sidebar-foreground">
                {company?.name ?? "Business Dite Ads"}
              </p>
              <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("subscription.planLabel", { tier: tier ?? "starter" })}
              </p>
            </div>
          </div>

          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3 [scrollbar-gutter:stable]">
            {navSections.map((section) => {
              if (!canAccess(section.access)) return null;
              const SectionIcon = section.icon;

              // Single-link section (e.g. Dashboard)
              if (section.to && !section.items) {
                const active = location.pathname.startsWith(section.to);
                return (
                  <Link
                    key={section.labelKey}
                    to={section.to}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                    )}
                  >
                    <SectionIcon className="h-4 w-4" />
                    {t(section.labelKey)}
                  </Link>
                );
              }

              const items = section.items ?? [];
              const visibleItems = items.filter((item) => canAccess(item.access));
              if (visibleItems.length === 0) return null;

              const isOpen = openSections[section.labelKey] ?? false;
              const hasActiveChild = visibleItems.some(
                (item) => item.to && location.pathname.startsWith(item.to),
              );

              return (
                <div key={section.labelKey}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSections((prev) => ({ ...prev, [section.labelKey]: !isOpen }))
                    }
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      hasActiveChild
                        ? "text-sidebar-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                    )}
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-center gap-3">
                      <SectionIcon className="h-4 w-4" />
                      {t(section.labelKey)}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform",
                        isOpen ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-sidebar-border pl-3">
                      {visibleItems.map((item) => {
                        const Icon = item.icon;
                        if (item.to) {
                          const active = location.pathname.startsWith(item.to);
                          return (
                            <Link
                              key={item.labelKey}
                              to={item.to}
                              className={cn(
                                "flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors",
                                active
                                  ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                              )}
                            >
                              <Icon className="h-3.5 w-3.5" />
                              {t(item.labelKey)}
                            </Link>
                          );
                        }
                        return (
                          <div
                            key={item.labelKey}
                            className="flex cursor-not-allowed items-center justify-between gap-3 rounded-md px-3 py-1.5 text-sm text-sidebar-foreground/40"
                            title={t("common.comingSoon")}
                          >
                            <span className="flex items-center gap-3">
                              <Icon className="h-3.5 w-3.5" />
                              {t(item.labelKey)}
                            </span>
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
                              {t("common.soon")}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="border-t border-sidebar-border p-3">
            <SidebarAdsCarousel />
            <div className="mb-2 flex items-center gap-3 rounded-md px-3 py-2">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-primary-foreground"
                style={{ background: "var(--gradient-brand)" }}
              >
                {(profile?.full_name ?? profile?.email ?? "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-medium text-sidebar-foreground">
                    {profile?.full_name ?? t("common.user")}
                  </p>
                  {primaryRole && (
                    <span
                      className={cn(
                        "shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
                        roleBadgeClass[primaryRole] ?? "bg-muted text-muted-foreground",
                      )}
                    >
                      {t(`roles.${primaryRole}`, { defaultValue: primaryRole })}
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">{profile?.email}</p>
              </div>
            </div>
            {!isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="mb-1 w-full justify-start gap-2 text-sidebar-foreground/80"
                onClick={openPortal}
                disabled={portalLoading}
              >
                <CreditCard className="h-4 w-4" />
                {portalLoading ? t("subscription.opening") : t("subscription.manage")}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-sidebar-foreground/80"
              onClick={async () => {
                await signOut();
                navigate({ to: "/auth" });
              }}
            >
              <LogOut className="h-4 w-4" />
              {t("common.signOut")}
            </Button>
          </div>
        </aside>

        {/* Backdrop on mobile */}
        {mobileOpen && (
          <button
            className="fixed inset-0 z-20 bg-black/40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label={t("nav.closeMenu")}
          />
        )}

        {/* Main */}
        <main className="min-h-screen flex-1 pt-14 lg:ml-64 lg:pt-0">
          <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
            <SubscriptionStatusPanel />
            <OwnerCompanyBar />
            <CompanySwitcher />
            <Outlet />
          </div>
        </main>
      </div>
    </OwnerCompanyProvider>
  );
}
